import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Eye, ArrowLeftRight, History } from "lucide-react";
import EditIcon from "@material-ui/icons/Edit";
import MaterialTable from "material-table";
import type { Column } from "material-table";
import { materialTableES } from "../../../../services/materialTableES";
import { Badge } from "../../../../components/Badge";
import { formatNumberToPrice } from "../../../../helpers/priceFormatting";
import styles from "./ProductsTable.module.scss";
import { ProductDetails } from "../ProductDetails/ProductDetails";
import {
  useGetInventory,
  useTransferStock,
} from "../../../../utils/react-query/queryInventory/queries";
import { useMyPermissions } from "../../../../utils/react-query/queryRbac/queries";
import {
  type SalesProduct,
  type InventoryTableRow,
  type StorageAddressRef,
  type TransferPayload,
} from "./types";
import { StockTransferModal } from "../StockTransferModal/StockTransferModal";
import { TransferHistoryModal } from "../TransferHistoryModal/TransferHistoryModal";
import { useAuth } from "../../../../services/auth";
import { successToast, errorToast } from "../../../../services/toasts";
import { useFilterStore } from "../../../../supplierEcommerce/store";

export type ProductStatus = "in-stock" | "low-stock" | "out-of-stock";

export const getProductStatus = (unitsAvailable: number): ProductStatus => {
  if (unitsAvailable === 0) return "out-of-stock";
  if (unitsAvailable < 10) return "low-stock";
  return "in-stock";
};

const getStatusBadge = (status: ProductStatus) => {
  switch (status) {
    case "in-stock":
      return { variant: "success" as const, label: "En Stock" };
    case "low-stock":
      return { variant: "warning" as const, label: "Stock Bajo" };
    case "out-of-stock":
      return { variant: "danger" as const, label: "Agotado" };
  }
};

/**
 * Builds dynamic MaterialTable columns — one per storage address.
 * Pure function: same inputs always produce the same column definitions.
 *
 * @param storageAddresses - list of locations from GET /v2/inventory
 * @param onTransfer - callback to open the transfer modal for a given product
 */
export function buildInventoryColumns(
  storageAddresses: StorageAddressRef[],
  onTransfer: (product: InventoryTableRow) => void
): Column<InventoryTableRow>[] {
  return storageAddresses.map((address) => ({
    title: address.name,
    field: `stock_${address.id}`,
    type: "numeric" as const,
    render: (rowData: InventoryTableRow) => {
      const stock = rowData.stockByLocation.find(
        (s) => s.storage_address_id === address.id
      );
      return stock?.quantity ?? 0;
    },
  }));
}

interface ProductsTableProps {
  searchQuery: string;
}

export function ProductsTable({ searchQuery }: ProductsTableProps) {
  const history = useHistory();
  const { isSupplier } = useAuth();
  const filter = useFilterStore((state) => state.filter);
  // Inventory hook — always active
  const {
    data: inventoryData,
    isLoading: isLoadingInventory,
    refetch: refetchInventory,
  } = useGetInventory();

  const { mutateAsync: executeTransfer } = useTransferStock();

  // RBAC — permission gate for stock transfer
  const {
    data: permissions = new Set<string>(),
    isLoading: permissionsLoading,
  } = useMyPermissions();

  const canTransfer =
    !permissionsLoading && permissions.has("inventory:transfer");

  // State for ProductDetails side panel
  const [selectedProduct, setSelectedProduct] = useState<SalesProduct | null>(
    null
  );
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  // State for StockTransferModal
  const [transferProduct, setTransferProduct] =
    useState<InventoryTableRow | null>(null);
  const [isTransferOpen, setIsTransferOpen] = useState(false);

  // State for TransferHistoryModal
  const [historyProduct, setHistoryProduct] =
    useState<InventoryTableRow | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  /** Products prepared for the inventory multi-location table */
  const inventoryTableData = useMemo<InventoryTableRow[]>(() => {
    if (!inventoryData?.products) return [];
    return inventoryData.products.map((product) => {
      const unitsAvailable = product.stockByLocation.reduce(
        (sum, s) => sum + s.quantity,
        0
      );
      return {
        ...product,
        units_available: unitsAvailable,
        status: getProductStatus(unitsAvailable),
      };
    });
  }, [inventoryData]);

  const storageAddresses = useMemo<StorageAddressRef[]>(
    () => inventoryData?.storageAddresses ?? [],
    [inventoryData]
  );

  /** Dynamic columns built from the list of storage addresses */
  const inventoryColumns = useMemo(
    () =>
      buildInventoryColumns(storageAddresses, (product) => {
        setTransferProduct(product);
        setIsTransferOpen(true);
      }),
    [storageAddresses]
  );

  const handleTransfer = async (payload: TransferPayload) => {
    try {
      await executeTransfer(payload);
      refetchInventory();
      setIsTransferOpen(false);
      successToast("Stock trasladado exitosamente");
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Error al trasladar el stock";
      errorToast(message);
    }
  };

  const filteredInventoryData = inventoryTableData.filter((product) => {
    const normalizedQuery = searchQuery.toLowerCase();
    const matchesSearch =
      !searchQuery ||
      product.name.toLowerCase().includes(normalizedQuery) ||
      product.line.toLowerCase().includes(normalizedQuery);

    // color and material are not part of InventoryProduct's declared type but
    // arrive on the API response; cast through any to access them safely.
    const productColor = (product as any).color as string | undefined;
    const productMaterial = (product as any).material as string | undefined;

    const matchesColor =
      filter.color.length === 0 || filter.color.includes(productColor ?? "");
    const matchesBrand =
      filter.brand.length === 0 || filter.brand.includes(product.brand ?? "");
    const matchesMaterial =
      filter.material.length === 0 ||
      filter.material.includes(productMaterial ?? "");
    const matchesLine =
      filter.line.length === 0 || filter.line.includes(product.line);
    const matchesPrice =
      product.price >= filter.price[0] && product.price <= filter.price[1];

    return (
      matchesSearch &&
      matchesColor &&
      matchesBrand &&
      matchesMaterial &&
      matchesLine &&
      matchesPrice
    );
  });

  const baseColumns: Column<InventoryTableRow>[] = [
    {
      title: "Estado",
      field: "status",
      render: (rowData) => {
        const { variant, label } = getStatusBadge(rowData.status);
        return <Badge variant={variant}>{label}</Badge>;
      },
      cellStyle: { whiteSpace: "nowrap" },
    },
    {
      title: "Línea",
      field: "line",
      render: (rowData) => (
        <span className={styles.lineBadge}>{rowData.line}</span>
      ),
      cellStyle: { whiteSpace: "nowrap" },
    },
    {
      title: "Nombre",
      field: "name",
      render: (rowData) => (
        <div>
          <div className={styles.productInfo}>{rowData.name}</div>
          <div className={styles.productId}>ID: {rowData.id}</div>
        </div>
      ),
    },
    {
      title: "Precio sin IVA",
      field: "price",
      type: "numeric",
      render: (rowData) => (
        <span className={styles.stockText}>
          {formatNumberToPrice(rowData.price)}
        </span>
      ),
    },
    {
      title: "Total disponible",
      field: "units_available",
      type: "numeric",
      render: (rowData) => (
        <span
          className={`${styles.stockText} ${
            rowData.units_available === 0
              ? styles.stockZero
              : rowData.units_available < 10
              ? styles.stockLow
              : styles.stockNormal
          }`}
        >
          {rowData.units_available}
        </span>
      ),
    },
  ];

  const allColumns = [...baseColumns, ...inventoryColumns];

  return (
    <div className={styles.container}>
      {storageAddresses.length === 0 && !isLoadingInventory && (
        <p className={styles.emptyState}>
          No hay bodegas configuradas. Agrega una dirección de bodega en la
          configuración del proveedor.
        </p>
      )}
      <MaterialTable
        title=""
        columns={allColumns}
        data={filteredInventoryData}
        isLoading={isLoadingInventory}
        actions={[
          {
            icon: () => <Eye className={styles.actionIcon} />,
            tooltip: "View details",
            onClick: (_event, rowData) => {
              const product = rowData as InventoryTableRow;
              // Map InventoryTableRow to SalesProduct shape for ProductDetails
              setSelectedProduct({
                id: String(product.id),
                name: product.name,
                line: product.line,
                price: product.price,
                units_available: product.units_available,
                status: product.status,
                widthCm: product.widthCm ?? null,
                lengthCm: product.lengthCm ?? null,
                areaM2: product.areaM2 ?? null,
                warehouseStock: 0,
                freeZoneStock: 0,
                imageUrl: product.imageUrl ?? undefined,
                brand: product.brand ?? null,
                complementaries: product.complementaries,
              } as SalesProduct);
              setIsDetailsOpen(true);
            },
          },
          ...(canTransfer
            ? [
                {
                  icon: () => <ArrowLeftRight className={styles.actionIcon} />,
                  tooltip: "Transfer stock",
                  onClick: (_event: any, rowData: any) => {
                    const product = rowData as InventoryTableRow;
                    setTransferProduct(product);
                    setIsTransferOpen(true);
                  },
                },
                {
                  icon: () => <History className={styles.actionIcon} />,
                  tooltip: "Transfer history",
                  onClick: (_event: any, rowData: any) => {
                    setHistoryProduct(rowData as InventoryTableRow);
                    setIsHistoryOpen(true);
                  },
                },
              ]
            : []),
          ...(isSupplier
            ? [
                {
                  icon: () => <EditIcon className={styles.actionIcon} />,
                  tooltip: "Edit product",
                  onClick: (_event: any, rowData: any) => {
                    const product = rowData as InventoryTableRow;
                    history.push(`/proveedor/producto/${product.id}/editar`);
                  },
                },
              ]
            : []),
        ]}
        options={{
          search: false,
          toolbar: false,
          paging: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          actionsColumnIndex: -1,
          padding: "dense",
          headerStyle: {
            backgroundColor: "#f4f6f8",
            color: "#637381",
            fontSize: "14px",
            fontWeight: 600,
            height: "3.5rem",
          },
          rowStyle: {
            fontSize: "14px",
            fontWeight: 400,
            color: "#1C252E",
          },
        }}
        localization={materialTableES}
      />

      <ProductDetails
        product={selectedProduct}
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
      />

      {isTransferOpen && transferProduct && (
        <StockTransferModal
          open={isTransferOpen}
          product={transferProduct}
          storageAddresses={storageAddresses}
          defaultFromAddressId={storageAddresses[0]?.id ?? 0}
          onClose={() => setIsTransferOpen(false)}
          onTransfer={handleTransfer}
        />
      )}

      {isHistoryOpen && historyProduct && (
        <TransferHistoryModal
          open={isHistoryOpen}
          product={historyProduct}
          onClose={() => setIsHistoryOpen(false)}
        />
      )}
    </div>
  );
}
